import { Component, inject } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { ThemePalette } from "@angular/material/core";
import { ActivatedRoute } from "@angular/router";
import { NEVER, of } from "rxjs";
import { catchError, finalize, switchMap, map } from "rxjs/operators";
import {
  CstFormBuilder,
  CstFormGroup,
} from "src/app/_uncategorised_models_services/cst-form-group";
import { FormControlBase } from "src/app/common-components/form-controls/form-control-base";
import { CstResponsivePopupComponentBase } from "src/app/common-components/resp-popup";
import { Claims } from "src/app/constants";
import { CstMailingService } from "src/app/mailing/mailing.service";
import { UserService } from "src/app/services/user.service";
import {
  VorgangUpdateCommand,
  InfoportalFoldersClientProvider,
  ValidationResult,
  VorgangDetailIncludes,
  DocumentVisibilitySettings,
  IVorgangUpdateCommand,
} from "src/app/web-api/web-api";

@Component({
    selector: "folder-insert",
    templateUrl: "./folder-update.component.html",
    styleUrls: ["./folder-update.component.scss"],
    standalone: false
})
export class InfoportalFolderUpdateComponent extends CstResponsivePopupComponentBase {
  private _fb = inject(CstFormBuilder);
  private _client = inject(InfoportalFoldersClientProvider);
  private _route = inject(ActivatedRoute);
  _userService = inject(UserService);
  private _mailService = inject(CstMailingService);

  formCanBeEdited = false;
  userIsStrictlyReadOnly = true;
  public form: CstFormGroup<IVorgangUpdateCommand>;
  sliderColor: ThemePalette = "warn";
  public loading = false;

  public hasDocuments = false;
  documentVisibilitySettings = DocumentVisibilitySettings;

  public constructor() {
    super();

    const params = this._route.snapshot.queryParams;
    if (params.folderID) {
      this.loading = true;
      this._client
        .getSingle(params.folderID, VorgangDetailIncludes.Dokumente)
        .subscribe((result) => {
          this.form.patchValue({
            ...result,
            tagIDs: result.tags?.map((x) => x.id),
          });
          this.hasDocuments = result.dokumente?.length > 0;
          this.loading = false;
        });
    }
    if (params.readonly) {
      this.formCanBeEdited = params.readonly === "1" ? false : true;
    }

    this.handleClaims();
    this.form = this._fb.group<IVorgangUpdateCommand>({
      id: new FormControl(null, Validators.required),
      bezeichnung: new FormControl(null, [
        Validators.required,
        Validators.maxLength(75),
        Validators.minLength(2),
      ]),
      info: new FormControl(null),
      status: new FormControl(null, Validators.required),
      setDocumentVisibility: new FormControl(
        DocumentVisibilitySettings.NoChanges,
        Validators.required,
      ),
      tagIDs: new FormControl([]),
    });
  }

  onAccept() {
    if (!this.form.valid) {
      return;
    }
    this.loading = true;
    const cmd = new VorgangUpdateCommand(this.form.value);
    this._client
      .vorgangUpdate(cmd)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
        catchError((error) => {
          if (error instanceof ValidationResult) {
            setTimeout(
              () =>
                FormControlBase.ApplyValidationResultToForm(this.form, error),
              200,
            );
            return;
          }
          console.error(error);
          return NEVER;
        }),
        switchMap((result) => {
          if (!result.folderVisibilityChangedMailData) {
            return of(result);
          }
          return this._mailService
            .displaySendMailDialog(result.folderVisibilityChangedMailData, {
              title: $localize`:@@Infoportal_FolderUpdate_SetExternalMailDialogTitle:Send mail to external users`,
              canBeDismissedByClickingOutsideDialogOrEscape: false,
            })
            .pipe(map(() => result));
        }),
      )
      .subscribe((result) => {
        this.close(result);
      });
  }

  onCancel() {
    this.close();
  }

  public toggleFormCanBeEdited(): void {
    this.formCanBeEdited = !this.formCanBeEdited;
  }

  private handleClaims(): void {
    if (this._userService.CurrentUser.internalAccess === false) {
      this.formCanBeEdited = false;
      this.userIsStrictlyReadOnly = true;
      return;
    }
    if (this._userService.HasClaim(Claims.InfoportalAPI_VorgangUpdate)) {
      this.userIsStrictlyReadOnly = false;
    }
  }
}
